import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Trash2, VolumeX, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { LiveBadge } from "../components/Brand";
import { useAuth } from "../context/AuthContext";

function ChatMessageItem({ msg, isMod, currentUserId, onDelete, onMute }) {
  return (
    <div className="bg-[#101B42]/80 border border-white/5 rounded-lg p-3 text-sm text-slate-200 group relative" data-testid="chat-message-item">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`font-semibold text-xs ${msg.user_role !== "participant" ? "text-[#F5A623]" : "text-[#00C8FF]"}`}>
          {msg.user_name}
          {msg.user_role === "moderator" && <span className="ml-1.5 text-[9px] font-data uppercase bg-[#F5A623]/20 text-[#F5A623] px-1.5 py-0.5 rounded">Mod</span>}
          {msg.user_role === "admin" && <span className="ml-1.5 text-[9px] font-data uppercase bg-[#F5A623]/20 text-[#F5A623] px-1.5 py-0.5 rounded">Org</span>}
        </span>
        <span className="text-[10px] text-slate-500 font-data">{new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <p className="break-words">{msg.text}</p>
      {isMod && (
        <div className="absolute top-2 right-2 hidden group-hover:flex gap-1 bg-[#0C1530] rounded-md p-0.5 border border-[#00C8FF]/30">
          <button onClick={() => onDelete(msg.id)} title="Excluir mensagem" data-testid="moderator-delete-msg-btn"
            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded">
            <Trash2 size={13} />
          </button>
          {msg.user_id !== currentUserId && (
            <button onClick={() => onMute(msg.user_id)} title="Silenciar por 5 min" data-testid="moderator-mute-user-btn"
              className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded">
              <VolumeX size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MessageList({ messages, isMod, currentUserId, onDelete, onMute, bottomRef }) {
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-1.5 max-h-[420px]" data-testid="chat-messages-list">
      {messages.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">Seja o primeiro a comentar!</p>
      )}
      {messages.map((m) => (
        <ChatMessageItem key={m.id} msg={m} isMod={isMod} currentUserId={currentUserId} onDelete={onDelete} onMute={onMute} />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

function ChatInput({ onSend }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    await onSend(text.trim());
    setText("");
    setSending(false);
  };

  return (
    <form onSubmit={submit} className="p-3 border-t border-[#00C8FF]/15 bg-[#0C1530] flex gap-2">
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500}
        placeholder="Escreva sua mensagem..." className="input-dark !py-2.5 text-sm" data-testid="chat-input" />
      <button type="submit" disabled={sending || !text.trim()} className="btn-primary !px-4 !py-2.5" data-testid="chat-send-button" aria-label="Enviar">
        <Send size={16} />
      </button>
    </form>
  );
}

function NoticesList({ announcements }) {
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[480px]" data-testid="room-notices-list">
      {announcements.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Nenhum aviso no momento.</p>}
      {announcements.map((a) => (
        <div key={a.id} className="bg-[#008EFF]/20 border border-[#00C8FF] rounded-lg p-3 text-cyan-200 text-sm font-medium flex items-start gap-2">
          <Megaphone size={15} className="mt-0.5 shrink-0" />
          <div>
            <p>{a.text}</p>
            <p className="text-[10px] text-cyan-400/70 font-data mt-1">{new Date(a.created_at).toLocaleString("pt-BR")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function VimeoPlayer({ room }) {
  if (!room.embed_url) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center p-8 gradient-brand">
        <p className="text-xs font-data tracking-[0.3em] uppercase text-[#00C8FF] mb-3">Transmissão</p>
        <p className="text-xl font-heading text-white">A transmissão desta sala começará em breve.</p>
        <p className="text-sm text-slate-400 mt-2">Fique de olho nos avisos da organização.</p>
      </div>
    );
  }
  return (
    <iframe
      src={`${room.embed_url}${room.embed_url.includes("?") ? "&" : "?"}autoplay=0&title=0&byline=0&portrait=0`}
      className="w-full h-full"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      title={room.name}
    />
  );
}

export default function Room() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tab, setTab] = useState("chat");
  const bottomRef = useRef(null);
  const isMod = user && (user.role === "admin" || user.role === "moderator");

  const lastTsRef = useRef(null);
  const pollCountRef = useRef(0);

  const fetchRoomMeta = useCallback(async () => {
    try {
      const [roomsRes, annRes] = await Promise.all([api.get("/rooms"), api.get("/announcements")]);
      setRoom(roomsRes.data.find((r) => r.id === id) || null);
      setAnnouncements(annRes.data);
    } catch (err) {
      console.error("Falha ao carregar dados da sala:", err);
    }
  }, [id]);

  const fetchMessages = useCallback(async (full = false) => {
    try {
      const params = !full && lastTsRef.current ? { after: lastTsRef.current } : {};
      const res = await api.get(`/rooms/${id}/messages`, { params });
      if (full) {
        setMessages(res.data);
        lastTsRef.current = res.data.length ? res.data[res.data.length - 1].created_at : null;
        return;
      }
      if (res.data.length) {
        lastTsRef.current = res.data[res.data.length - 1].created_at;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = res.data.filter((m) => !seen.has(m.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch (err) {
      console.error("Falha ao carregar mensagens:", err);
    }
  }, [id]);

  const load = useCallback(async () => {
    await Promise.all([fetchRoomMeta(), fetchMessages(true)]);
  }, [fetchRoomMeta, fetchMessages]);

  useEffect(() => {
    load();
    const fast = setInterval(() => {
      pollCountRef.current += 1;
      fetchMessages(pollCountRef.current % 6 === 0);
    }, 4000);
    const slow = setInterval(fetchRoomMeta, 20000);
    return () => { clearInterval(fast); clearInterval(slow); };
  }, [load, fetchMessages, fetchRoomMeta]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = useCallback(async (text) => {
    try {
      await api.post(`/rooms/${id}/messages`, { text });
      await fetchMessages();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [id, fetchMessages]);

  const deleteMsg = useCallback(async (msgId) => {
    try {
      await api.delete(`/mod/rooms/${id}/messages/${msgId}`);
      setMessages((m) => m.filter((x) => x.id !== msgId));
      toast.success("Mensagem removida");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [id]);

  const mute = useCallback(async (userId) => {
    try {
      await api.post("/mod/mute", { user_id: userId, minutes: 5 });
      toast.success("Participante silenciado por 5 minutos");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  if (!room) {
    return <div className="text-slate-400 text-sm" data-testid="room-loading">Carregando sala...</div>;
  }

  return (
    <div data-testid="live-room">
      <button onClick={() => navigate("/lobby")} className="btn-ghost inline-flex items-center gap-1.5 mb-4 text-sm" data-testid="back-to-lobby">
        <ArrowLeft size={16} /> Voltar ao lobby
      </button>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white">{room.name}</h1>
        <LiveBadge live={room.is_live} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8">
          <div className="rounded-xl overflow-hidden border border-[#00C8FF]/25 bg-black aspect-video" data-testid="vimeo-player-container">
            <VimeoPlayer room={room} />
          </div>
          {room.description && <p className="text-sm text-slate-400 mt-4">{room.description}</p>}
        </div>

        <div className="lg:col-span-4 flex flex-col bg-[#101B42]/60 border border-[#00C8FF]/20 rounded-xl overflow-hidden min-h-[480px]">
          <div className="grid grid-cols-2 bg-[#0C1530]">
            <button onClick={() => setTab("chat")} data-testid="room-tab-chat"
              className={`py-3 text-sm font-bold uppercase tracking-wider transition-colors ${tab === "chat" ? "text-[#00C8FF] border-b-2 border-[#00C8FF]" : "text-slate-500"}`}>
              Chat ao vivo
            </button>
            <button onClick={() => setTab("avisos")} data-testid="room-tab-notices"
              className={`py-3 text-sm font-bold uppercase tracking-wider transition-colors ${tab === "avisos" ? "text-[#00C8FF] border-b-2 border-[#00C8FF]" : "text-slate-500"}`}>
              Avisos
            </button>
          </div>
          {tab === "chat" ? (
            <>
              <MessageList messages={messages} isMod={isMod} currentUserId={user.id} onDelete={deleteMsg} onMute={mute} bottomRef={bottomRef} />
              <ChatInput onSend={send} />
            </>
          ) : (
            <NoticesList announcements={announcements} />
          )}
        </div>
      </div>
    </div>
  );
}
